/* eslint-disable import/exports-last */
/* eslint-disable import/prefer-default-export */

import { IMPLICIT_ROLES, INPUT_TYPE_ROLES } from './ariaRoles'
import type { AriaRole } from 'react'

type HTMLTags = keyof typeof IMPLICIT_ROLES
type InputRoles = keyof typeof INPUT_TYPE_ROLES

const FOCUSABLE_ELEMENTS = ['a', 'button', 'input', 'textarea', 'select', 'details', 'area', 'label']

function getIdRefs(ids: string | null | undefined) {
  if (!ids || !ids.trim()) {
    return new Set<string>()
  }
  return new Set(ids.trim().split(/\s+/))
}

export function getAriaDescribedBy(oldDescribedBy: string | null, id: string): string {
  const uniqueIds = getIdRefs(oldDescribedBy)
  return [...uniqueIds.add(id)].join(' ')
}

export function getAriaRole(element: Element): AriaRole {
  const explicitRole = element.getAttribute('role')
  if (explicitRole) return explicitRole

  const tagName = element.tagName.toLowerCase() as HTMLTags

  if (tagName === 'input') {
    const inputType = element.getAttribute('type') as InputRoles
    return inputType ? INPUT_TYPE_ROLES[inputType] : 'textbox'
  }
  const role = IMPLICIT_ROLES[tagName]

  if (!role) {
    return 'button'
  }

  return role
}

export function findFocusableChildren(
  node?: Element | null,
  list = FOCUSABLE_ELEMENTS
): NodeListOf<HTMLElement> | undefined {
  if (!node || !(node instanceof HTMLElement)) return
  return node.querySelectorAll(`${list.join(',')},[tabindex]:not([tabindex="-1"])`)
}

function isFocusable(node?: Element | null, list = FOCUSABLE_ELEMENTS): boolean {
  if (!node) return false
  const tabindex = node.getAttribute('tabindex')
  const invalid = (!tabindex && !list.includes(node.tagName.toLowerCase())) || (tabindex && parseInt(tabindex) < 0)
  if (invalid) return false
  if (node.hasAttribute('disabled')) return false
  const styles = window.getComputedStyle(node)
  if (styles.display === 'none') return false
  if (styles.visibility === 'hidden') return false
  return true
}

export function findFirstFocusableChild(node?: Element | null, list = FOCUSABLE_ELEMENTS): HTMLElement | null {
  if (!node || !(node instanceof HTMLElement)) return null
  const elements = node.querySelectorAll(
    `${list.join(',')}:not([tabindex="-1"]):not([disabled]):not([inert]),[tabindex]:not([tabindex="-1"]):not([disabled]):not([inert])`
  )
  for (const ele of elements) {
    if (isFocusable(ele, list)) {
      return ele as HTMLElement
    }
  }
  return null
}

export function findFirstFocusableChildOrRoot(node?: Element | null, list = FOCUSABLE_ELEMENTS): HTMLElement | null {
  if (!node || !(node instanceof HTMLElement)) return null
  if (isFocusable(node, list)) return node
  return findFirstFocusableChild(node, list)
}

function findSibling(
  getSibling: (node?: Element | null) => Element | null,
  node?: Element | null,
  list = FOCUSABLE_ELEMENTS
) {
  if (!node || !(node instanceof HTMLElement)) return null
  let current: Element | null = node
  let result: Element | null = null
  while ((current = getSibling(current)) && !(result = findFirstFocusableChildOrRoot(current, list))) {
    /* empty */
  }
  return result as HTMLElement | null
}

export function findNextFocusableSibling(node?: Element | null, list = FOCUSABLE_ELEMENTS): HTMLElement | null {
  return findSibling((node) => node?.nextElementSibling ?? null, node, list)
}

export function findPreviousFocusableSibling(node?: Element | null, list = FOCUSABLE_ELEMENTS): HTMLElement | null {
  return findSibling((node) => node?.previousElementSibling ?? null, node, list)
}

export function findNextFocusableElementExcludingChildren(
  node?: Element | null,
  list = FOCUSABLE_ELEMENTS
): HTMLElement | null {
  if (!node || !(node instanceof HTMLElement)) return null
  let current: HTMLElement | null = node
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const sibling = findNextFocusableSibling(current, list)
    if (sibling) return sibling
    current = current?.parentElement ?? null
    if (!current || current === node) return null
  }
}

/**
 * We should use this to whenever we want to act on the popup content immediately after calling setOpen.
 * We need to wait until 'open' state changes are propagated and element is rendered.
 * We put the resolve on the stack twice: first through setTimeout, then window.requestAnimationFrame,
 * to offset the indirect nature of useEffect updating the {TriggerRect} reacting to the 'open' state
 * The "proper" way to do it would be use a mutation observer on the parent, but this is way cheaper
 * and as long as the indirectness is NOT changed (ie more useEffect put between the setOpen and trigger rectangle updates)
 * it will work
 * @returns {Promise<void>}
 */
export async function tick() {
  return new Promise<void>((resolve) => {
    setTimeout(() => window.requestAnimationFrame(() => resolve()))
  })
}

export const POPUP_CONTAINER_ATTR = 'data-popup'
export const POPUP_TRIGGER_ATTR = 'data-popup-id'

export function findElementPopupParent(element?: Element | null) {
  let current = element
  while (current) {
    if (current?.hasAttribute(POPUP_CONTAINER_ATTR)) return current
    current = current?.parentElement ?? null
  }
  return null
}

export function isElementTriggerWithinTarget(target?: Element | null, element?: Element | null) {
  if (!element || !target) return false
  const popupElement = findElementPopupParent(element)
  if (!popupElement) return false
  const popupId = popupElement.getAttribute(POPUP_CONTAINER_ATTR)
  if (!popupId) return false
  if (target.getAttribute(POPUP_TRIGGER_ATTR) === popupId) return true
  const targetPopup = findElementPopupParent(target)
  if (!targetPopup) return false
  const trigger = targetPopup.querySelector(`[${POPUP_TRIGGER_ATTR}="${popupId}"]`)
  return !!trigger
}

export function getScrollParent(node: Element | null) {
  if (!node) {
    return null
  }

  if ((node.clientHeight && node.scrollHeight > node.clientHeight) || node.tagName === 'DIALOG') {
    if (node.tagName === 'HTML') return window
    return node
  }
  return getScrollParent(node.parentNode as Element | null)
}
